<!-- src/views/TrendsView.vue -->
<!-- 회원권 추세: 월별 추세·이탈 위험·체험 전환·운영 지표 네 탭 -->
<!-- (docs/architecture/admin-trends-design.md 4절). 머리·탭·기간·[새로고침]·URL 쿼리·탭별 불러오기·상태 문구·지표 정의 -->
<template>
  <div class="trends-page">
    <!-- 상단: 제목 · 기간 · 몇 시 기준 · [새로고침] / 탭 · 계산 간격 -->
    <div class="head">
      <h2 class="title">회원권 추세</h2>

      <!-- 기간은 이탈 위험 탭에서 쓰지 않는다(오늘 기준이라 기간이 없다). 그 자리는 같은 높이의 한 줄로 두어, 390px처럼
           기간 줄이 탭 위에 따로 있는 화면에서 탭을 오갈 때 탭 줄이 오르내리지 않게 한다(리뷰 2차) -->
      <div v-if="usesMonths" class="periods" role="group" aria-label="기간">
        <button
          v-for="m in monthOptions"
          :key="m"
          type="button"
          :class="['period-btn', { active: months === m }]"
          :aria-pressed="months === m"
          @click="setMonths(m)"
        >
          {{ m }}개월
        </button>
      </div>
      <p v-else class="periods-none">오늘 기준 목록이라 기간이 없습니다</p>

      <p class="as-of" aria-live="polite">
        <span v-if="asOf">{{ asOf }}</span>
        <span v-if="contactsAsOf" class="as-of-sub">{{ contactsAsOf }}</span>
      </p>

      <button
        type="button"
        class="refresh-btn"
        :disabled="!canRefresh"
        :aria-busy="activeLoading"
        :title="refreshTitle"
        @click="refresh"
      >
        새로고침
      </button>

      <div class="tabs-row">
        <!-- ←/→로 탭 사이 초점을 옮기고 Enter·Space로 연다(지나가는 탭마다 불러오지 않게) -->
        <div class="tabs" role="tablist" aria-label="회원권 추세 보기">
          <button
            v-for="(t, i) in tabs"
            :id="tabId(t.key)"
            :key="t.key"
            type="button"
            role="tab"
            :class="['tab', { active: tab === t.key }]"
            :aria-selected="tab === t.key ? 'true' : 'false'"
            :aria-controls="panelId(t.key)"
            :tabindex="tab === t.key ? 0 : -1"
            @click="selectTab(t.key)"
            @keydown="onTabKeydown($event, i)"
          >
            {{ t.label }}
          </button>
        </div>
        <p class="recompute-note">{{ recomputeText }}</p>
      </div>
    </div>

    <!-- [새로고침]의 "이미 최신 계산입니다"는 그 (탭, 기간)에서만 보인다(다른 탭으로 옮기면 그 탭 값이 아니다).
         화면 아래에 떠서 보이고 자리를 차지하지 않는다(리뷰 3차: 표 위에 끼워 목록이 51px 내려갔다가 3초 뒤 저절로 올라가
         누르려던 줄이 바뀌었다). 누름은 아래 화면으로 지나간다 -->
    <p
      v-if="latestMessage && latestKey === activeKey"
      class="flash"
      role="status"
    >
      {{ latestMessage }}
    </p>

    <!-- 탭 내용: 한 번 연 탭은 그대로 두고(안의 고른 상태 유지) 보이기만 바꾼다 -->
    <div
      v-for="t in tabs"
      v-show="tab === t.key"
      :id="panelId(t.key)"
      :key="t.key"
      class="tab-panel"
      role="tabpanel"
      :aria-labelledby="tabId(t.key)"
    >
      <template v-if="views[t.key].kind !== 'idle'">
        <!-- 알림: DB 보호 때문에 앞서 계산한 값, 다시 읽기 실패, 다른 기간을 읽는 중. 다른 기간을 읽는 중은 흐린 앞 값 위에
             겹쳐 떠서 자리를 차지하지 않는다(리뷰 3차: 위에 끼웠다가 값이 오면 빠져 화면이 저절로 올라갔다) -->
        <div
          v-if="views[t.key].fallback && !views[t.key].error"
          class="notice notice--info notice--float"
          role="status"
        >
          {{ months }}개월 값을 불러오는 중…
        </div>
        <div v-if="staleText(views[t.key])" class="notice notice--info">
          {{ staleText(views[t.key]) }}
        </div>
        <!-- 자정을 넘겨 열어 둔 화면: 어제 계산한 값이라고 알린다(다시 읽지는 않는다, P5) -->
        <div v-if="previousDayText(views[t.key])" class="notice notice--info">
          {{ previousDayText(views[t.key]) }}
          <button
            type="button"
            class="link-btn"
            :disabled="!canRetry(views[t.key].key)"
            @click="retry(t.key)"
          >
            새로고침
          </button>
        </div>
        <!-- 값을 보이는 채 받은 오류: DB 보호 멈춤(503)은 아래 멈춤 상태처럼 파랑 안내, 진짜 실패만 노랑(R30, 리뷰 5차).
             다른 기간을 읽다 실패해 앞 기간 값을 흐리게 두면 그 값이 어느 기간·언제 값인지 덧붙인다(리뷰 6차) -->
        <div
          v-if="views[t.key].kind !== 'error' && views[t.key].error"
          :class="['notice', { 'notice--info': isPaused(views[t.key]) }]"
        >
          {{ errorText(views[t.key]) }}
          <span v-if="fallbackText(views[t.key])" class="fallback-note">{{
            fallbackText(views[t.key])
          }}</span>
          <button
            type="button"
            class="link-btn"
            :disabled="!canRetry(views[t.key].key)"
            @click="retry(t.key)"
          >
            다시 시도
          </button>
        </div>

        <div v-if="views[t.key].kind === 'loading'" class="state">
          불러오는 중…
        </div>

        <!-- DB 보호로 멈춤(503)은 고장이 아니라 안내라 파랑(옛 값을 보일 때의 안내와 같은 색), 진짜 실패만 빨강 -->
        <div
          v-else-if="views[t.key].kind === 'error'"
          :class="[
            'state',
            isPaused(views[t.key]) ? 'state--paused' : 'state--error',
          ]"
        >
          <p>{{ errorText(views[t.key]) }}</p>
          <button
            type="button"
            class="retry-btn"
            :disabled="!canRetry(views[t.key].key)"
            @click="retry(t.key)"
          >
            다시 시도
          </button>
        </div>

        <div v-else-if="views[t.key].kind === 'empty'" class="state">
          {{ emptyMessages[t.key] }}
        </div>

        <div
          v-else-if="views[t.key].kind === 'data'"
          :class="[
            'panel-body',
            {
              'panel-body--refreshing':
                views[t.key].dim && !views[t.key].fallback,
              'panel-body--fallback': views[t.key].fallback,
            },
          ]"
        >
          <TrendsOverview
            v-if="t.key === 'overview'"
            :data="views[t.key].data"
            :months="views[t.key].data.months || months"
          />
          <AtRiskPanel
            v-else-if="t.key === 'at-risk'"
            ref="atRiskPanel"
            :data="views[t.key].data"
            :requested-at="requestedAt[views[t.key].key] || 0"
            :reason="reason"
            :contact="contact"
            @update:reason="setReason"
            @update:contact="setContact"
            @contact-saved="onContactSaved"
          />
          <TrialFunnelPanel
            v-else-if="t.key === 'trials'"
            :data="views[t.key].data"
          />
          <OperationsPanel
            v-else-if="t.key === 'operations'"
            :data="views[t.key].data"
            :months="views[t.key].data.months || months"
          />
        </div>
      </template>

      <!-- 지표 정의: 그 탭에서 쓰는 말의 긴 정의(툴팁이 아니라 늘 보인다). 처음 불러오는 동안은 그리지 않는다:
           "불러오는 중…" 바로 아래 그렸다가 값이 오면 수천 px 아래로 밀려 화면이 크게 튀었다 -->
      <section
        v-if="views[t.key].kind !== 'loading'"
        class="definitions"
        :aria-labelledby="`trends-def-${t.key}`"
      >
        <h3 :id="`trends-def-${t.key}`" class="def-title">지표 정의</h3>
        <dl class="def-list">
          <div
            v-for="d in definitionsByTab[t.key]"
            :key="d.key"
            class="def-row"
          >
            <dt>{{ d.term }}</dt>
            <dd>{{ d.text }}</dd>
          </div>
        </dl>
      </section>
    </div>
  </div>
</template>

<script>
import TrendsOverview from "@/components/trends/TrendsOverview.vue";
import AtRiskPanel from "@/components/trends/AtRiskPanel.vue";
import TrialFunnelPanel from "@/components/trends/TrialFunnelPanel.vue";
import OperationsPanel from "@/components/trends/OperationsPanel.vue";
import {
  DEFAULT_CONTACT,
  DEFAULT_MONTHS,
  DEFAULT_REASON,
  DEFAULT_TAB,
  MONTH_OPTIONS,
  TABS,
  TAB_FETCHERS,
  alreadyLatestMessage,
  asOfLabel,
  buildTrendsQuery,
  contactFromNote,
  definitionOf,
  durationLabel,
  fallbackNote,
  isTrendsPaused,
  kstToday,
  parseTrendsQuery,
  previousDayNote,
  recomputeNote,
  retryAfterSeconds,
  staleNote,
  tabUsesMonths,
  trendsErrorMessage,
} from "@/lib/trends";

// [새로고침]이 같은 계산을 받았을 때 "이미 최신 계산입니다"를 보이는 시간
const LATEST_MESSAGE_MS = 3000;

// 탭마다 맨 아래 "지표 정의"에 보이는 말(lib/trends.js METRIC_DEFINITIONS의 키, 4.9절)
const DEFINITION_TERMS = {
  active: "활성 회원",
  new: "신규",
  returned: "복귀",
  renewal_rate: "재등록률",
  churn_rate: "이탈률",
  pending: "판정 대기",
  early_renewed: "미리 재등록",
  overlap: "겹침",
  decisions_open: "집계 중",
  revenue: "매출",
  revenue_kind: "신규·재등록·복귀 매출",
  upcoming: "다음 달 이후 시작",
  in_progress: "진행 중",
  no_data: "기록 전",
  no_data_trials: "기록 전",
  no_data_operations: "기록 전",
  small_sample: "표본 적음",
  expiring: "만료 임박",
  grace: "재등록 대기",
  low_count: "횟수 소진 임박",
  absent: "출석 공백",
  churned: "최근 이탈",
  urgent: "오늘 연락 · 지켜보기",
  contact_state: "연락 상태",
  reason_count: "이유별 수",
  applied: "신청",
  contacted: "연락",
  scheduled: "체험 예약",
  attended: "체험 참석",
  registered: "등록",
  registration_rate: "등록률",
  attended_registration_rate: "체험 참석 → 등록률",
  registered_direct: "바로 등록",
  existing_members: "기존 회원",
  cancelled: "취소",
  failures: "자동 예약 실패",
  avg_days_to_register: "등록까지 일수",
  registration_open: "등록 집계 중",
  follow_ups: "참석 후 미등록",
  per_member_week: "1인 주간 출석",
  no_show: "불참률",
  use_up: "횟수권 소진율",
  fill: "정원 채움률",
  slots: "요일 × 시작 시각",
  not_in_schedule: "지금 시간표에 없음",
};

const DEFINITION_KEYS = {
  overview: [
    "active",
    "new",
    "returned",
    "renewal_rate",
    "churn_rate",
    "pending",
    "early_renewed",
    "overlap",
    "decisions_open",
    "revenue",
    "revenue_kind",
    "upcoming",
    "in_progress",
    "no_data",
    "small_sample",
  ],
  "at-risk": [
    "expiring",
    "grace",
    "low_count",
    "absent",
    "churned",
    "urgent",
    "contact_state",
    "reason_count",
  ],
  trials: [
    "applied",
    "contacted",
    "scheduled",
    "attended",
    "registered",
    "registration_rate",
    "attended_registration_rate",
    "registered_direct",
    "existing_members",
    "cancelled",
    "failures",
    "avg_days_to_register",
    "registration_open",
    "follow_ups",
    "in_progress",
    "no_data_trials",
    "small_sample",
  ],
  operations: [
    "per_member_week",
    "no_show",
    "use_up",
    "fill",
    "slots",
    "not_in_schedule",
    "in_progress",
    "no_data_operations",
    "small_sample",
  ],
};

const EMPTY_MESSAGES = {
  overview: "고른 기간에 수강권 기록이 없습니다.",
  "at-risk": "지금 연락이 필요한 회원이 없습니다.",
  trials: "고른 기간에 무료체험 신청이 없습니다.",
  operations: "고른 기간에 끝난 수업이 없습니다.",
};

function firstOf(v) {
  return Array.isArray(v) ? v[0] : v;
}

function sameQuery(a, b) {
  const ka = Object.keys(a);
  const kb = Object.keys(b || {});
  if (ka.length !== kb.length) return false;
  return ka.every((k) => String(firstOf(b[k])) === a[k]);
}

function positive(n) {
  return typeof n === "number" && Number.isFinite(n) && n > 0;
}

// 받은 값이 비었는지(탭마다 빈 상태 문구를 보인다, 4.3~4.6절)
function isEmptyData(tab, d) {
  if (!d) return true;
  const rows = Array.isArray(d.rows) ? d.rows : [];
  switch (tab) {
    case "overview": {
      const any = rows.some((r) => {
        const dec = r.decisions || {};
        return (
          positive(r.active) ||
          positive(r.new) ||
          positive(r.returned) ||
          positive(r.revenue && r.revenue.count) ||
          positive(dec.renewed) ||
          positive(dec.early_renewed) ||
          positive(dec.churned) ||
          positive(dec.pending) ||
          positive(dec.overlap)
        );
      });
      const upcoming = d.current && d.current.upcoming;
      return !any && !positive(upcoming && upcoming.count);
    }
    case "at-risk":
      return !(Array.isArray(d.members) && d.members.length > 0);
    case "trials":
      return (
        !rows.some((r) => positive(r.requests)) &&
        !(Array.isArray(d.follow_ups) && d.follow_ups.length > 0)
      );
    case "operations":
      return (
        !rows.some(
          (r) =>
            positive(r.fill && r.fill.classes) ||
            positive(r.no_show && r.no_show.booked) ||
            positive(r.attendance && r.attendance.attended)
        ) && !(Array.isArray(d.slots) && d.slots.length > 0)
      );
    default:
      return false;
  }
}

export default {
  name: "TrendsView",
  components: {
    TrendsOverview,
    AtRiskPanel,
    TrialFunnelPanel,
    OperationsPanel,
  },
  data() {
    return {
      tabs: TABS,
      monthOptions: MONTH_OPTIONS,
      emptyMessages: EMPTY_MESSAGES,

      // 지금 상태(URL 쿼리 ?tab=&months=&reason=&contact=와 같다)
      tab: DEFAULT_TAB,
      months: DEFAULT_MONTHS,
      // 이탈 위험 거르기. 다른 탭에 가도 기억해 두고, 주소에는 이탈 위험 탭에서만 쓴다
      reason: DEFAULT_REASON,
      contact: DEFAULT_CONTACT,

      // 읽은 값: 키(탭|기간, 이탈 위험은 탭만) → 응답. 화면이 살아 있는 동안 다시 읽지 않는다(P5)
      store: {},
      // 키 → 그 응답을 부른 때(브라우저 시계 ms). 이탈 위험 탭이 저장한 연락보다 뒤에 부른 응답인지 가린다
      requestedAt: {},
      loadingKeys: {},
      errors: {},
      // 503(DB 보호로 멈춤) 뒤 Retry-After가 끝나는 시각(ms). 그동안 같은 키의 [다시 시도]·[새로고침]을 막는다
      pausedUntil: {},
      // 늦게 온 옛 응답을 버리려는 키별 요청 번호
      seqs: {},
      // 탭마다 마지막으로 보인 값의 키(다른 기간을 읽는 동안·읽다 실패한 동안 흐리게 보일 값)
      lastLoaded: {},
      nowMs: Date.now(),
      // 브라우저의 오늘(KST). "HH:mm 기준"에 날짜를 붙일지, 어제 계산한 값인지를 이것과 견준다(응답의 today가 아니라).
      // 1분마다 날짜만 다시 본다(서버는 부르지 않는다, P5)
      todayKst: kstToday(),
      latestMessage: "",
      latestKey: "",
    };
  },
  computed: {
    usesMonths() {
      return tabUsesMonths(this.tab);
    },
    activeKey() {
      return this.keyOf(this.tab, this.months);
    },
    activeLoading() {
      return !!this.loadingKeys[this.activeKey];
    },
    // 탭마다 무엇을 그릴지: loading·error·empty·data·idle(아직 열지 않음)
    views() {
      const out = {};
      for (const t of TABS) out[t.key] = this.viewOf(t.key);
      return out;
    },
    activeView() {
      return this.views[this.tab];
    },
    // "14:20 기준"(그 탭 응답의 generated_at). 계산한 날이 브라우저의 오늘이 아니면 "10.16 07:33 기준"(자정을 넘겨 열어 둔 화면).
    // 다른 기간 값을 흐리게 보이는 동안은 쓰지 않는다(자리는 .as-of의 최소 크기로 지켜 기간 버튼·탭이 움직이지 않게)
    asOf() {
      const v = this.activeView;
      if (!v || !v.data || v.fallback) return "";
      return asOfLabel(v.data.generated_at, this.todayKst);
    },
    // 이탈 위험: 연락 기록을 다시 읽지 못했으면 "연락 기록 14:20 기준"
    contactsAsOf() {
      const v = this.activeView;
      if (this.tab !== "at-risk" || !v || !v.data) return "";
      if (v.data.contacts_fresh !== false) return "";
      const at = asOfLabel(v.data.contacts_at, this.todayKst);
      return at ? `연락 기록 ${at}` : "";
    },
    // 머리의 계산 간격: 서버가 응답에 준 recompute_minutes(입력 캐시 TTL)로 만든다(화면에 박아 두지 않음, 사장님 결정 2026-10-16).
    // TTL은 모든 탭이 같으므로 지금 탭 값이 아직 없으면 받은 다른 응답의 값을 쓴다
    recomputeText() {
      const v = this.activeView;
      const own = v && v.data ? recomputeNote(v.data) : "";
      if (own) return own;
      for (const d of Object.values(this.store)) {
        const text = recomputeNote(d);
        if (text) return text;
      }
      return "";
    },
    canRefresh() {
      return !this.activeLoading && this.pausedLeft(this.activeKey) === 0;
    },
    refreshTitle() {
      const left = this.pausedLeft(this.activeKey);
      if (left > 0) return `${left}초 뒤 다시 누를 수 있습니다`;
      const v = this.activeView;
      const floor = durationLabel(v && v.data && v.data.refresh_minutes);
      return floor
        ? `지금 탭을 바로 다시 계산합니다(${floor} 안에 계산한 값이면 그대로)`
        : "지금 탭을 바로 다시 계산합니다(방금 계산한 값이면 그대로)";
    },
    definitionsByTab() {
      const out = {};
      for (const t of TABS) {
        out[t.key] = (DEFINITION_KEYS[t.key] || [])
          .map((key) => ({
            key,
            term: DEFINITION_TERMS[key] || key,
            text: definitionOf(key),
          }))
          .filter((d) => d.text);
      }
      return out;
    },
  },
  watch: {
    // 뒤로 가기·탭·기간 버튼 모두 여기서 읽는다
    "$route.query"() {
      // 다른 화면으로 나가는 중이면 무시
      if (this.$route.name !== "Trends") return;
      this.applyRouteQuery();
    },
  },
  created() {
    this.applyRouteQuery();
  },
  // 이탈 위험 [연락 기록] 모달에 쓰던 내용이 있으면 앱 안에서 다른 화면으로 나가기 전에 묻는다(신규회원 관리와 같음).
  // 새로 고침·탭 닫기는 onBeforeUnload(브라우저 확인 창)가 묻는다
  beforeRouteLeave() {
    const panel = this.atRiskPanel();
    if (
      panel &&
      typeof panel.hasDraft === "function" &&
      panel.hasDraft() &&
      typeof panel.confirmDiscard === "function" &&
      !panel.confirmDiscard()
    ) {
      return false;
    }
    return true;
  },
  // 같은 화면 안에서 주소만 바뀌어(뒤로·앞으로 가기로 다른 /trends 항목, 사이드메뉴 "회원권 추세") 이탈 위험 탭을 떠나면
  // [연락 기록] 모달이 숨은 탭 안에 남는다. 쓰던 글이 있으면 묻고([취소]면 머묾), 떠나면 모달을 닫는다(리뷰 2차: 글이 묻지 않고
  // 숨었고, 숨은 모달이 Tab을 막고 Esc가 보이지 않는 창을 물었다)
  beforeRouteUpdate(to) {
    if (parseTrendsQuery(to.query).tab === "at-risk") return true;
    const panel = this.atRiskPanel();
    if (!panel) return true;
    if (
      typeof panel.hasDraft === "function" &&
      panel.hasDraft() &&
      typeof panel.confirmDiscard === "function" &&
      !panel.confirmDiscard()
    ) {
      return false;
    }
    if (typeof panel.closeContactLog === "function") panel.closeContactLog();
    return true;
  },
  // 폴링·visibilitychange·focus로 다시 읽지 않는다(P5). 창 닫기 확인만 건다
  mounted() {
    window.addEventListener("beforeunload", this.onBeforeUnload);
    this.clockTimer = setInterval(this.tickDate, 60 * 1000);
  },
  beforeUnmount() {
    window.removeEventListener("beforeunload", this.onBeforeUnload);
    if (this.ticker) clearInterval(this.ticker);
    this.ticker = null;
    if (this.latestTimer) clearTimeout(this.latestTimer);
    this.latestTimer = null;
    if (this.clockTimer) clearInterval(this.clockTimer);
    this.clockTimer = null;
  },
  methods: {
    tabId(key) {
      return `trends-tab-${key}`;
    },
    panelId(key) {
      return `trends-panel-${key}`;
    },
    // 이탈 위험은 기간과 상관없이 한 번
    keyOf(tab, months) {
      return tabUsesMonths(tab) ? `${tab}|${months}` : tab;
    },

    viewOf(tab) {
      const key = this.keyOf(tab, this.months);
      const loading = !!this.loadingKeys[key];
      const error = this.errors[key] || null;
      const own = this.store[key];
      if (own) {
        return {
          kind: isEmptyData(tab, own) ? "empty" : "data",
          key,
          data: own,
          dim: loading,
          fallback: false,
          error,
        };
      }
      if (loading) {
        // 다른 기간을 읽는 동안은 앞서 본 값을 흐리게 둔다(자리가 튀지 않게)
        const last = this.lastLoaded[tab];
        const prev = last && last !== key ? this.store[last] : null;
        if (prev && !isEmptyData(tab, prev)) {
          return {
            kind: "data",
            key: last,
            data: prev,
            dim: true,
            fallback: true,
            error: null,
          };
        }
        return { kind: "loading", key, error: null };
      }
      if (error) {
        // 다른 기간을 읽다 실패하면(503 멈춤 포함) 앞서 보던 값을 흐린 채 두고 그 위에 알림을 보인다: 오류 상태로 바꾸면 패널이
        // 내려가 탭 안에서 고른 상태(추세의 종류, 운영의 기간·기준)를 잃었다(리뷰 5차). key는 실패한 기간(카운트다운·[다시 시도])
        const last = this.lastLoaded[tab];
        const prev = last && last !== key ? this.store[last] : null;
        if (prev && !isEmptyData(tab, prev)) {
          return {
            kind: "data",
            key,
            data: prev,
            dim: true,
            fallback: true,
            error,
          };
        }
        return { kind: "error", key, error };
      }
      // 다른 탭에서 기간을 바꾼 동안 숨은 탭은 앞서 읽은 값을 그대로 둔다: idle이면 패널이 내려가 안에서 고른 상태
      // (추세의 종류, 운영의 기간·기준)를 잃는다. 그 탭을 다시 열면 새 기간을 읽는 동안 이 값이 흐리게 보인다
      if (tab !== this.tab) {
        const last = this.lastLoaded[tab];
        const prev = last ? this.store[last] : null;
        if (prev) {
          return {
            kind: isEmptyData(tab, prev) ? "empty" : "data",
            key: last,
            data: prev,
            dim: false,
            fallback: true,
            error: null,
          };
        }
      }
      return { kind: "idle", key, error: null };
    },

    // ---------------- URL 쿼리 ----------------

    applyRouteQuery() {
      const q = parseTrendsQuery(this.$route.query);
      this.tab = q.tab;
      this.months = q.months;
      if (q.tab === "at-risk") {
        this.reason = q.reason;
        this.contact = q.contact;
      }
      // 잘못된 값·기본값은 주소에서 고쳐 둔다(뒤로 가기·회원 상세에서 돌아올 주소가 깔끔하게)
      const canonical = buildTrendsQuery(this.currentState());
      if (!sameQuery(canonical, this.$route.query)) {
        this.$router.replace({ query: canonical }).catch(() => {});
      }
      this.ensureLoaded();
    },

    currentState() {
      return {
        tab: this.tab,
        months: this.months,
        reason: this.reason,
        contact: this.contact,
      };
    },

    // 상태를 URL에 담는다. 주소가 바뀌면 watch가 읽는다
    updateQuery(patch) {
      const next = { ...this.currentState(), ...patch };
      const query = buildTrendsQuery(next);
      if (sameQuery(query, this.$route.query)) {
        this.ensureLoaded();
        return;
      }
      this.$router.replace({ query }).catch(() => {});
    },

    selectTab(key) {
      if (key === this.tab) return;
      this.updateQuery({ tab: key });
    },
    setMonths(m) {
      if (m === this.months) return;
      this.updateQuery({ months: m });
    },
    setReason(value) {
      this.updateQuery({ reason: value });
    },
    setContact(value) {
      this.updateQuery({ contact: value });
    },

    // ←/→·Home·End는 초점만 옮긴다(Enter·Space로 연다)
    onTabKeydown(event, index) {
      const n = this.tabs.length;
      let next = null;
      if (event.key === "ArrowRight") next = (index + 1) % n;
      else if (event.key === "ArrowLeft") next = (index - 1 + n) % n;
      else if (event.key === "Home") next = 0;
      else if (event.key === "End") next = n - 1;
      if (next === null) return;
      event.preventDefault();
      const el = document.getElementById(this.tabId(this.tabs[next].key));
      if (el) el.focus();
    },

    // ---------------- 불러오기 (P5: 탭을 처음 열 때만, (탭, 기간)마다 한 번) ----------------

    ensureLoaded() {
      const key = this.activeKey;
      // 다른 기간을 읽는 동안 흐리게 둘 값은 마지막으로 보인 값이다(마지막으로 받은 응답이 아니라. 리뷰 5차)
      if (this.store[key]) this.lastLoaded[this.tab] = key;
      if (this.store[key] || this.loadingKeys[key] || this.errors[key]) return;
      this.load(this.tab, this.months);
    },

    async load(tab, months, { refresh = false } = {}) {
      const key = this.keyOf(tab, months);
      if (this.pausedLeft(key) > 0) return;
      const fetcher = TAB_FETCHERS[tab];
      if (!fetcher) return;
      const seq = (this.seqs[key] || 0) + 1;
      this.seqs[key] = seq;
      const before = this.store[key] ? this.store[key].generated_at : "";
      this.loadingKeys[key] = true;
      const startedMs = Date.now();
      try {
        // [새로고침]은 refresh=1: 서버가 새로고침 바닥(refresh_minutes)보다 오래된 계산이면 바로 다시 계산한다
        const res = await fetcher(months, { refresh });
        // 늦게 온 옛 응답은 버린다
        if (this.seqs[key] !== seq) return;
        this.requestedAt[key] = startedMs;
        this.store[key] = res || {};
        this.errors[key] = null;
        // 그 탭이 지금 보이는 기간의 응답만 "마지막으로 보인 값"이 된다. 이미 떠난 기간의 늦은 응답은 저장만 한다(리뷰 5차:
        // 12 → 24 → 12로 바꾼 뒤 늦게 온 24개월 값이 다음 기간을 읽는 동안 흐리게 보여 화면이 1,300px 늘었다 줄었다)
        if (key === this.keyOf(tab, this.months)) this.lastLoaded[tab] = key;
        // 방금 계산한 값이라(새로고침 바닥 안) 같은 계산이 왔으면 다음 계산 시각(서버의 refresh_at)을 알린다(P2).
        // DB 보호 때문에 받은 옛 값(stale)이면 알리지 않는다: "이미 최신"이 아니고 다음 계산 시각도 이미 지났다(탭 안 staleNote가 설명한다)
        if (
          refresh &&
          before &&
          res &&
          res.generated_at === before &&
          !res.stale
        ) {
          this.showLatestMessage(alreadyLatestMessage(res), key);
        }
      } catch (e) {
        if (this.seqs[key] !== seq) return;
        console.error("회원권 추세 불러오기 실패:", e);
        this.errors[key] = e;
        if (isTrendsPaused(e)) {
          this.pausedUntil[key] = Date.now() + retryAfterSeconds(e) * 1000;
          this.startTicker();
        }
      } finally {
        if (this.seqs[key] === seq) this.loadingKeys[key] = false;
      }
    },

    // 지금 탭의 지금 기간만 다시 계산한다(refresh=1, 방금 계산한 값이면 서버가 그대로 준다)
    refresh() {
      if (!this.canRefresh) return;
      this.load(this.tab, this.months, { refresh: true });
    },

    retry(tab) {
      const key = this.keyOf(tab, this.months);
      if (!this.canRetry(key)) return;
      this.load(tab, this.months, { refresh: !!this.store[key] });
    },

    canRetry(key) {
      return !this.loadingKeys[key] && this.pausedLeft(key) === 0;
    },

    // ---------------- 503 카운트다운 ----------------

    pausedLeft(key) {
      const until = this.pausedUntil[key];
      if (!until) return 0;
      return Math.max(0, Math.ceil((until - this.nowMs) / 1000));
    },

    // 카운트다운 글자만 1초마다 바꾼다(서버를 다시 부르지 않음). 멈춘 키가 없으면 그친다
    startTicker() {
      this.nowMs = Date.now();
      if (this.ticker) return;
      this.ticker = setInterval(() => {
        this.nowMs = Date.now();
        const anyPaused = Object.values(this.pausedUntil).some(
          (t) => t > this.nowMs
        );
        if (!anyPaused) {
          clearInterval(this.ticker);
          this.ticker = null;
        }
      }, 1000);
    },

    isPaused(view) {
      return !!(view && view.error && isTrendsPaused(view.error));
    },

    errorText(view) {
      if (!view || !view.error) return "";
      return trendsErrorMessage(view.error, this.pausedLeft(view.key));
    },

    staleText(view) {
      if (!view || !view.data || view.fallback) return "";
      return staleNote(view.data);
    },

    // 다른 기간을 읽다 실패해 흐리게 둔 앞 기간 값: "아래 흐린 값은 앞서 본 12개월 값입니다(14:20 기준)."
    fallbackText(view) {
      if (!view || !view.fallback || !view.error) return "";
      return fallbackNote(view.data, this.todayKst);
    },

    previousDayText(view) {
      if (!view || !view.data || view.fallback) return "";
      return previousDayNote(view.data, this.todayKst);
    },

    // 날짜가 바뀌었을 때만 바꾼다(1분마다 다시 그리지 않게)
    tickDate() {
      const d = kstToday();
      if (d !== this.todayKst) this.todayKst = d;
    },

    showLatestMessage(text, key) {
      this.latestMessage = text;
      this.latestKey = key;
      if (this.latestTimer) clearTimeout(this.latestTimer);
      this.latestTimer = setTimeout(() => {
        this.latestMessage = "";
        this.latestTimer = null;
      }, LATEST_MESSAGE_MS);
    },

    // ---------------- 이탈 위험 연락 기록 ----------------

    atRiskPanel() {
      const ref = this.$refs.atRiskPanel;
      return Array.isArray(ref) ? ref[0] || null : ref || null;
    },

    // [연락 기록] 모달이 저장한 기록으로 그 줄의 최근 연락만 바꾼다(다시 읽지 않음, R20)
    onContactSaved(payload) {
      const { userId, note } = payload || {};
      const d = this.store[this.keyOf("at-risk", this.months)];
      if (!d || !Array.isArray(d.members) || !note) return;
      const row = d.members.find((m) => m.user_id === userId);
      if (row) row.contact = contactFromNote(note);
    },

    onBeforeUnload(event) {
      const panel = this.atRiskPanel();
      if (!panel || typeof panel.hasDraft !== "function" || !panel.hasDraft())
        return;
      // 모달의 전화번호(tel:)를 눌러 전화를 거는 것은 떠나는 것이 아니다
      if (typeof panel.isDialing === "function" && panel.isDialing()) return;
      event.preventDefault();
      event.returnValue = "";
    },
  },
};
</script>

<style scoped>
.trends-page {
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: #151718;
  border-radius: 10px;
  padding: 16px;
  /* 넓은 표(상자 안 가로 스크롤)의 최소 너비가 MainLayout의 1fr 칸을 밀어 페이지 전체가 가로로 넘쳤다(390px에서 827px).
     너비 0 + 최소 100%로 자기 내용 너비를 칸 계산에 넣지 않고, 칸 너비를 그대로 채운다 */
  width: 0;
  min-width: 100%;
  box-sizing: border-box;
  color: #fff;
}

/* ---------------- 상단 ---------------- */
.head {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto auto;
  grid-template-areas:
    "title periods asof refresh"
    "tabs tabs tabs tabs";
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.title {
  grid-area: title;
  margin: 0;
  font-size: 24px;
  font-weight: 400;
  color: #fff;
  white-space: nowrap;
}

/* 기간 버튼 (붙어 있는 한 묶음, 신규회원 관리와 같은 모양) */
.periods {
  grid-area: periods;
  display: inline-flex;
  border: 1px solid #333;
  border-radius: 6px;
  overflow: hidden;
}

/* 이탈 위험 탭: 기간 버튼 묶음과 같은 자리·같은 높이(버튼 36px + 위아래 테두리 1px)의 한 줄 */
.periods-none {
  grid-area: periods;
  display: flex;
  align-items: center;
  box-sizing: border-box;
  height: 38px;
  margin: 0;
  color: #9ca3af;
  font-size: 13px;
  white-space: nowrap;
}

.period-btn {
  height: 36px;
  min-width: 64px;
  padding: 0 12px;
  border: 0;
  border-left: 1px solid #333;
  background: #1b1d1e;
  color: #9d9d9d;
  font-size: 14px;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.2s, color 0.2s;
}

.period-btn:first-child {
  border-left: 0;
}

.period-btn:hover:not(.active) {
  background: #252728;
  color: #fff;
}

.period-btn.active {
  background: #78ff47;
  color: #000;
  font-weight: 600;
}

/* 묶음의 overflow: hidden이 바깥 초점 테두리를 자르므로 안쪽에 그린다 */
.period-btn:focus-visible {
  outline: 2px solid #fff;
  outline-offset: -2px;
}

.period-btn.active:focus-visible {
  outline-color: #000;
}

/* 다른 탭·기간을 불러오는 동안 글자가 비어도 칸 너비를 지킨다("10.16 07:33 기준"이 드는 너비): 칸이 0으로 줄면
   기간 버튼이 버튼 하나만큼 옆으로 밀려 [24개월] 두 번 누름이 [12개월]에 떨어졌다 */
.as-of {
  grid-area: asof;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  min-width: 108px;
  margin: 0;
  font-size: 13px;
  color: #c8c8c8;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.as-of-sub {
  font-size: 12px;
  color: #fbbf24;
}

.refresh-btn {
  grid-area: refresh;
  height: 36px;
  padding: 0 14px;
  border-radius: 6px;
  border: 1px solid #333;
  background: #1b1d1e;
  color: #fff;
  font-size: 14px;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.2s, border-color 0.2s, color 0.2s;
}

.refresh-btn:hover:not(:disabled) {
  border-color: #78ff47;
  color: #78ff47;
}

.refresh-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.refresh-btn:focus-visible {
  outline: 2px solid #78ff47;
  outline-offset: 2px;
}

.tabs-row {
  grid-area: tabs;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 6px 16px;
  min-width: 0;
  border-bottom: 1px solid #262626;
}

/* 탭 줄이 가로로 넘치면 그 줄만 안에서 스크롤한다(페이지 가로 스크롤 없음) */
.tabs {
  display: flex;
  gap: 2px;
  min-width: 0;
  max-width: 100%;
  overflow-x: auto;
  scrollbar-width: none;
}

.tabs::-webkit-scrollbar {
  display: none;
}

.tab {
  flex-shrink: 0;
  height: 42px;
  padding: 0 16px;
  border: 0;
  border-bottom: 2px solid transparent;
  background: transparent;
  color: #9d9d9d;
  font-size: 15px;
  white-space: nowrap;
  cursor: pointer;
  transition: color 0.2s, border-color 0.2s;
}

.tab:hover:not(.active) {
  color: #fff;
}

.tab.active {
  color: #fff;
  font-weight: 600;
  border-bottom-color: #78ff47;
}

.tab:focus-visible {
  outline: 2px solid #fff;
  outline-offset: -4px;
  border-radius: 6px;
}

/* 계산 간격은 첫 응답이 온 뒤에 채워진다(서버의 recompute_minutes). 그 전에도 한 줄 자리를 지켜 390px에서 탭 줄이 내려가지 않게 한다 */
.recompute-note {
  margin: 0 0 8px;
  font-size: 12px;
  line-height: 1.4;
  min-height: 1.4em;
  color: #8e8e8e;
  word-break: keep-all;
  overflow-wrap: anywhere;
}

/* 화면 아래 가운데에 뜨는 알림(자리를 차지하지 않고 누름을 막지 않는다). 모달(1000)보다 아래 */
.flash {
  position: fixed;
  left: 50%;
  bottom: 24px;
  z-index: 900;
  box-sizing: border-box;
  width: max-content;
  max-width: calc(100vw - 32px);
  margin: 0;
  padding: 10px 14px;
  border-radius: 8px;
  border: 1px solid rgba(96, 165, 250, 0.45);
  background: #10233d;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.5);
  color: #93c5fd;
  font-size: 13px;
  line-height: 1.5;
  word-break: keep-all;
  overflow-wrap: anywhere;
  transform: translateX(-50%);
  pointer-events: none;
}

/* ---------------- 탭 내용 ---------------- */
.tab-panel {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
}

/* 다른 기간을 읽는 동안: 흐린 앞 값(누를 수 없음) 맨 위에 겹쳐 뜬다 */
.notice.notice--float {
  position: absolute;
  top: 8px;
  left: 50%;
  z-index: 3;
  box-sizing: border-box;
  width: max-content;
  max-width: calc(100% - 16px);
  background: #10233d;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.5);
  transform: translateX(-50%);
  pointer-events: none;
}

.panel-body {
  min-width: 0;
  transition: opacity 0.15s;
}

/* 같은 값을 다시 읽는 중: 틀은 그대로 두고 살짝 흐리게 */
.panel-body--refreshing {
  opacity: 0.7;
}

/* 다른 기간을 읽는 동안 보이는 앞 기간 값: 더 흐리게, 누를 수 없게 */
.panel-body--fallback {
  opacity: 0.4;
  pointer-events: none;
}

.notice {
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid rgba(251, 191, 36, 0.35);
  background: rgba(251, 191, 36, 0.08);
  color: #fbbf24;
  font-size: 14px;
  line-height: 1.5;
  word-break: keep-all;
  overflow-wrap: anywhere;
}

.notice--info {
  border-color: rgba(96, 165, 250, 0.35);
  background: rgba(96, 165, 250, 0.08);
  color: #60a5fa;
}

.link-btn {
  margin-left: 6px;
  padding: 0;
  border: 0;
  background: none;
  color: #fff;
  font-size: 14px;
  text-decoration: underline;
  cursor: pointer;
}

.link-btn:disabled {
  color: #6b7280;
  cursor: not-allowed;
}

.state {
  padding: 40px 16px;
  border-radius: 10px;
  border: 1px dashed #2a2a2a;
  text-align: center;
  color: #c8c8c8;
  font-size: 15px;
  line-height: 1.5;
  word-break: keep-all;
  overflow-wrap: anywhere;
}

.state p {
  margin: 0 0 12px;
}

.state--error {
  color: #ef4444;
}

.state--paused {
  color: #60a5fa;
  border-color: rgba(96, 165, 250, 0.35);
  background: rgba(96, 165, 250, 0.06);
}

.retry-btn {
  height: 38px;
  padding: 0 16px;
  border-radius: 6px;
  border: 1px solid #333;
  background: #1b1d1e;
  color: #fff;
  cursor: pointer;
}

.retry-btn:hover:not(:disabled) {
  background: #252728;
}

.retry-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

/* ---------------- 지표 정의 ---------------- */
.definitions {
  margin-top: 8px;
  padding-top: 14px;
  border-top: 1px solid #262626;
}

.def-title {
  margin: 0 0 10px;
  font-size: 14px;
  font-weight: 600;
  color: #c8c8c8;
}

.def-list {
  display: grid;
  grid-template-columns: max-content minmax(0, 1fr);
  gap: 6px 16px;
  margin: 0;
}

/* 줄마다 dt·dd를 같은 격자에 놓는다 */
.def-row {
  display: contents;
}

.def-row dt {
  font-size: 13px;
  font-weight: 600;
  color: #e5e5e5;
  white-space: nowrap;
}

.def-row dd {
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
  color: #9d9d9d;
  word-break: keep-all;
  overflow-wrap: anywhere;
}

/* ---------------- 반응형 ---------------- */
@media (max-width: 720px) {
  .head {
    grid-template-columns: minmax(0, 1fr) auto;
    grid-template-areas:
      "title refresh"
      "asof asof"
      "periods periods"
      "tabs tabs";
    row-gap: 10px;
  }

  .title {
    font-size: 20px;
  }

  /* 비어 있어도 한 줄 높이를 지켜 아래 기간·탭 줄이 값이 올 때 내려가지 않게 한다 */
  .as-of {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: baseline;
    justify-content: flex-start;
    gap: 2px 10px;
    min-width: 0;
    min-height: 18px;
    line-height: 18px;
    white-space: normal;
  }

  .periods {
    display: flex;
    width: 100%;
  }

  .period-btn {
    flex: 1;
    min-width: 0;
  }

  /* 계산 간격 문구는 탭 위로(탭 밑줄이 줄의 아래 선에 붙게) */
  .tabs-row {
    flex-direction: column;
    align-items: stretch;
  }

  .recompute-note {
    order: -1;
    margin: 0;
  }

  .tab {
    padding: 0 12px;
    font-size: 14px;
  }

  .def-list {
    grid-template-columns: minmax(0, 1fr);
    gap: 2px;
  }

  .def-row dt {
    margin-top: 8px;
    white-space: normal;
  }
}
</style>
